import { useCallback, useMemo } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { type DataTableColumn, useDataTableColumns } from 'mantine-datatable';
import type { DocPabean } from '../../../app/(dashboard)/doc-pabean/apis/models/doc-pabean-model';
import {
  DEFAULT_DISPLAY_PREFS,
  DOC_PABEAN_COLUMNS,
  type DocPabeanDisplayPrefs,
  type DocPabeanTablePageKey,
  KOLOM_SEEN_KEY,
  displayPrefsKey,
  normalizeDisplayPrefs,
} from './doc-pabean-list-prefs';

// Stub columns built once from the shared list. The table and the Kolom picker
// both call useDataTableColumns with these, under the same key, so they read
// and write the same `<key>-columns-toggle` entry and Mantine's same-tab
// storage event keeps them in step. The table renders its own column
// definitions in code order and widths; only the toggle state is taken from
// here (see isColumnHidden).
const TOGGLE_COLUMNS: DataTableColumn<DocPabean>[] = DOC_PABEAN_COLUMNS.map((c) => ({
  accessor: c.accessor,
  toggleable: c.toggleable,
}));

/**
 * Column visibility for one page of the doc-pabean table. Stored in
 * localStorage by mantine-datatable (via Mantine's useLocalStorage, whose reads
 * and writes are wrapped in try/catch: blocked storage falls back to defaults).
 */
export function useDocPabeanColumnToggle(pageKey: DocPabeanTablePageKey) {
  const { columnsToggle, setColumnsToggle, resetColumnsToggle } = useDataTableColumns<DocPabean>({
    key: pageKey,
    columns: TOGGLE_COLUMNS,
    getInitialValueInEffect: false,
  });
  return { columnsToggle, setColumnsToggle, resetColumnsToggle };
}

/** Logo / flag switches for one page, under `doc-pabean-display:<page key>`. */
export function useDocPabeanDisplayPrefs(pageKey: DocPabeanTablePageKey) {
  const [stored, setStored] = useLocalStorage<DocPabeanDisplayPrefs>({
    key: displayPrefsKey(pageKey),
    defaultValue: DEFAULT_DISPLAY_PREFS,
    getInitialValueInEffect: false,
  });
  const prefs = useMemo(() => normalizeDisplayPrefs(stored), [stored]);
  const setPref = useCallback(
    (name: keyof DocPabeanDisplayPrefs, value: boolean) => setStored({ ...prefs, [name]: value }),
    [prefs, setStored]
  );
  const resetPrefs = useCallback(() => setStored({ ...DEFAULT_DISPLAY_PREFS }), [setStored]);
  return { prefs, setPref, resetPrefs };
}

/** `doc-pabean-kolom-seen = 1` once the picker has been opened in this browser. */
export function useKolomSeen() {
  const [seen, setSeen] = useLocalStorage<string>({
    key: KOLOM_SEEN_KEY,
    defaultValue: '',
    getInitialValueInEffect: false,
    serialize: (v) => v,
    deserialize: (v) => v ?? '',
  });
  const markSeen = useCallback(() => {
    if (seen !== '1') setSeen('1');
  }, [seen, setSeen]);
  return { seen: seen === '1', markSeen };
}
